import get from 'lodash/get';
import { PersonIdentificationDocType } from '@/modules/entity/components/common/PersonIdentificationDocuments.tsx';
import {
  AssociationEntityDetails,
  CompanyEntityDetails,
  EntityWithDocumentFullResponse,
  PartnershipEntityDetails,
  ShareholderDetails,
  TrustEntityDetails,
} from '@/modules/entity/types/EntityItemResponse.ts';
import { MappedTrusteeHolder } from '@/modules/issuer-dashboard/components/holder-create/tabs/MappedTrusteeHolder.tsx';
import { OptionalStepsOptions } from '@/modules/issuer-dashboard/components/holder-create/tabs/OptionalStepsHolder.tsx';
import { UserDetailsData } from 'api';

export type OnboardingStepProps = {
  moveForward?: () => void;
  allowSkip?: boolean;
  entity_type_prefix: string;
  person_details_key?:
    | keyof Pick<TrustEntityDetails, 'settlors' | 'trustee' | 'beneficiaries'>
    | keyof Pick<CompanyEntityDetails, 'shareholders' | 'directors'>
    | keyof Pick<PartnershipEntityDetails, 'stakeholders'>
    | keyof Pick<AssociationEntityDetails, 'officers'>;
  label?: string;
  conditionCallback?: (data: EntityWithDocumentFullResponse) => boolean;
  description?: string;
  roads?: OnboardingRoadItem[];
};

export interface ChangeShareholderComponentProps extends OnboardingStepProps {
  index?: number;
  title?: string;
  uuidIndex?: string;
  item_entity_type?: string;
  titleHint?: string;
}

export interface OptionalStepsProps extends OnboardingStepProps {
  title: string;
  options: OptionalStepsOptions[];
  subroad: ((props: OnboardingStepProps & { index?: number; title?: string }) => JSX.Element)[];
  field_name: string;
  index?: number;
  navName?: string;
}

interface GetNavProgressProps {
  data?: EntityWithDocumentFullResponse | undefined;
  user_data?: UserDetailsData | undefined;
  shareholder_id?: number | undefined;
  getSubStep: (step: number) => number;
  getPercentWithSubRoad: (shareholders: Array<any>, step: number, allSubStep?: number) => number;
  step: number;
}

interface GetNavProgressShareholderProps extends GetNavProgressProps {
  entity_type_prefix: string;
  person_details_key:
    | keyof Pick<TrustEntityDetails, 'settlors' | 'trustee' | 'beneficiaries'>
    | keyof Pick<CompanyEntityDetails, 'shareholders' | 'directors'>
    | keyof Pick<PartnershipEntityDetails, 'stakeholders'>
    | keyof Pick<AssociationEntityDetails, 'officers'>;
  child_person_details_key?: string;
  shareholdersPriority?: ShareholderDetails[];
}

export interface OnboardingRoadItem {
  component: (props: OptionalStepsProps) => JSX.Element;
  optionalStepsProps?: OptionalStepsProps;
  allowSkip?: boolean;
  entity_type_prefix: string;
  person_details_key?:
    | keyof Pick<TrustEntityDetails, 'settlors' | 'trustee' | 'beneficiaries'>
    | keyof Pick<CompanyEntityDetails, 'shareholders' | 'directors'>
    | keyof Pick<PartnershipEntityDetails, 'stakeholders'>
    | keyof Pick<AssociationEntityDetails, 'officers'>;
  label?: string;
  title?: string;
  conditionCallback?: (data: EntityWithDocumentFullResponse) => boolean;
  navName: string;
  getNavProgress: (params: GetNavProgressProps) => { percent: number; completed: boolean };
}

export const getNavProgressShareholder = ({
  data,
  getPercentWithSubRoad,
  entity_type_prefix,
  person_details_key,
  step,
  shareholdersPriority,
  child_person_details_key,
}: GetNavProgressShareholderProps): { percent: number; completed: boolean } => {
  const allSubStep = 2;
  const shareholders =
    shareholdersPriority ||
    (get(data, `${entity_type_prefix}.${person_details_key}`, []) as ShareholderDetails[]);
  const childShareholders = child_person_details_key
    ? (shareholders
        ?.map(item => get(item, child_person_details_key, []).map(value => value))
        .flat() as ShareholderDetails[])
    : shareholders;

  const percent = getPercentWithSubRoad(childShareholders, step, allSubStep);

  const completed =
    childShareholders?.every(person => {
      const personDocumentIndex = person?.index || '';
      const personalCompoundDocumentIndex = `${person_details_key}_${personDocumentIndex}`;
      const documentType = person_details_key
        ? `${person.type}_entity_details_identification_document`
        : `${entity_type_prefix}_identification_document`;
      const document = (data?.document_files || []).filter(
        item =>
          (person.type === 'individual' &&
            (item.type === PersonIdentificationDocType.Passport ||
              item.type?.includes(PersonIdentificationDocType.DriverLicense) ||
              item.type === PersonIdentificationDocType.Medicare) &&
            item.index === personalCompoundDocumentIndex) ||
          (person.type === 'company' &&
            ((item.type === documentType && !person_details_key) ||
              (item.index === personalCompoundDocumentIndex && !!person_details_key))),
      );

      const result =
        (((person?.type === 'individual' &&
          (!!person?.passport_number ||
            !!person?.driver_license_number ||
            !!person?.medicare_card_number) &&
          !!person?.date_of_birth) ||
          (person?.type === 'company' && !!person?.country)) &&
          document?.length > 0) ||
        !!person?.same_as_index;

      return result;
    }) || !childShareholders;

  return {
    percent,
    completed,
  };
};

interface CreateShareholderStepProps {
  entity_type_prefix: string;
  person_details_key:
    | keyof Pick<TrustEntityDetails, 'settlors' | 'trustee' | 'beneficiaries'>
    | keyof Pick<CompanyEntityDetails, 'shareholders' | 'directors'>
    | keyof Pick<PartnershipEntityDetails, 'stakeholders'>
    | keyof Pick<AssociationEntityDetails, 'officers'>;
  child_person_details_key?: string;
  navName: string;
}

export const createShareholderStep = ({
  entity_type_prefix,
  person_details_key,
  child_person_details_key,
  navName,
}: CreateShareholderStepProps): OnboardingRoadItem => {
  return {
    component: MappedTrusteeHolder,
    entity_type_prefix,
    person_details_key,
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix,
      label: 'Company extract document',
      field_name: child_person_details_key || person_details_key,
      title: '',
      subroad: [],
      options: [],
    },
    navName,
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key,
        entity_type_prefix,
        child_person_details_key,
      });
    },
  };
};
